import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { StatusPill } from "@/components/StatusPill";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { QRCodeSVG } from "qrcode.react";
import { ArrowLeft, Edit3, Trash2, Share2, MessageCircle, Star, Package, Copy } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function ProductDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, company } = useAuth();
  const [p, setP] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => { (async () => {
    try { const { data } = await api.get(`/products/${id}`); setP(data); } catch { toast.error("Not found"); }
  })(); }, [id]);

  if (!p) return <div className="text-center py-16 text-[#4A5A50]">Loading…</div>;

  const canEdit = ["admin", "staff"].includes(user?.role);
  const storefrontProductUrl = `${window.location.origin}/store/${company?.code}/products/${p.id}`;
  const previewUrl = `${process.env.REACT_APP_BACKEND_URL}/api/public/preview/product/${p.id}`;

  const share = () => setShareOpen(true);
  const copy = () => { navigator.clipboard.writeText(storefrontProductUrl); toast.success("Link copied"); };
  const whatsappShare = () => {
    const caption = `${p.title}${p.is_price_visible ? ` · ₹${(p.computed_price || 0).toLocaleString('en-IN')}` : ""}\n\n${p.description || ""}\n\nView here: ${storefrontProductUrl}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(caption)}`, "_blank", "noopener,noreferrer");
  };

  const del = async () => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`); toast.success("Deleted"); nav("/products");
  };

  return (
    <div className="space-y-6" data-testid="product-detail-page">
      <button onClick={() => nav(-1)} className="text-sm text-[#4A5A50] hover:text-[#0F1E15] inline-flex items-center gap-1"><ArrowLeft size={14}/> Back</button>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="aspect-square bg-[#F1EDDF] rounded-2xl overflow-hidden border border-[#E4DFCF]">
            {p.media?.[0] ? <img src={fileUrl(p.media[0].url)} alt="" className="w-full h-full object-cover cursor-zoom-in" onClick={() => setLightbox(p.media[0])}/> :
              <div className="w-full h-full flex items-center justify-center text-[#C9A24B]"><Package size={64} strokeWidth={1}/></div>}
          </div>
          {p.media?.length > 1 && (
            <div className="mt-2 grid grid-cols-6 gap-2">
              {p.media.map((m, i) => (
                <button key={i} onClick={() => setLightbox(m)} className="aspect-square rounded-lg overflow-hidden border border-[#E4DFCF]">
                  <img src={fileUrl(m.thumb_url || m.url)} alt="" className="w-full h-full object-cover"/>
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#4A5A50]">{p.metal} · {p.purity || "—"} {p.featured && <span className="ml-2 text-[#C9A24B]">★ Featured</span>}</div>
          <h1 className="font-serif text-4xl font-medium mt-1" data-testid="product-title">{p.title}</h1>
          <div className="text-sm text-[#4A5A50] mt-1">SKU: {p.sku || "—"}</div>
          <div className="mt-3"><StatusPill status={p.status === "in-stock" ? "accepted" : p.status === "made-to-order" ? "in-progress" : p.status === "sold" ? "completed" : "on-hold"}/></div>

          {p.is_price_visible && (
            <div className="mt-6">
              <div className="text-[10px] uppercase tracking-widest text-[#4A5A50]">{p.pricing_mode === "gold-rate-based" ? "Live price" : "Price"}</div>
              <div className="font-serif text-5xl font-medium text-[#C9A24B] mt-1">₹{(p.computed_price || 0).toLocaleString('en-IN')}</div>
              {p.pricing_mode === "gold-rate-based" && p.net_weight && (
                <div className="text-xs text-[#4A5A50] mt-1">Net {p.net_weight}g · Making ₹{(p.making_charge || 0).toLocaleString('en-IN')}</div>
              )}
            </div>
          )}

          <div className="mt-6 grid grid-cols-2 gap-3 text-sm">
            <div><div className="text-[10px] uppercase tracking-widest text-[#4A5A50]">Gross weight</div><div>{p.gross_weight || "—"} g</div></div>
            <div><div className="text-[10px] uppercase tracking-widest text-[#4A5A50]">Net weight</div><div>{p.net_weight || "—"} g</div></div>
            <div className="col-span-2"><div className="text-[10px] uppercase tracking-widest text-[#4A5A50]">Stones</div><div>{p.stone_details || "—"}</div></div>
          </div>

          {p.description && <div className="mt-4 text-[#0F1E15] whitespace-pre-line">{p.description}</div>}

          {p.tags?.length ? (
            <div className="mt-4 flex flex-wrap gap-1">
              {p.tags.map((t,i) => <span key={i} className="text-[10px] uppercase tracking-widest bg-[#F6EED8] text-[#6E5416] px-2 py-1 rounded-full">{t}</span>)}
            </div>
          ) : null}

          <div className="mt-6 flex flex-wrap gap-2">
            <Button variant="outline" onClick={share} className="rounded-full" data-testid="product-share-btn"><Share2 size={14} className="mr-1.5"/> Share</Button>
            <Button variant="outline" onClick={whatsappShare} className="rounded-full border-[#25D366] text-[#1B8B4B] hover:bg-[#25D366]/10" data-testid="product-whatsapp-btn"><MessageCircle size={14} className="mr-1.5"/> Share on WhatsApp</Button>
            {canEdit && <Button variant="outline" onClick={() => nav(`/products/${id}/edit`)} className="rounded-full" data-testid="product-edit-btn"><Edit3 size={14} className="mr-1.5"/> Edit</Button>}
            {user?.role === "admin" && <Button variant="outline" onClick={del} className="rounded-full text-red-600 border-red-200 hover:bg-red-50" data-testid="product-delete-btn"><Trash2 size={14} className="mr-1.5"/> Delete</Button>}
          </div>
        </div>
      </div>

      {lightbox && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6" onClick={() => setLightbox(null)}>
          <img src={fileUrl(lightbox.url)} alt="" className="max-w-full max-h-full rounded-xl"/>
        </div>
      )}

      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent data-testid="share-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Share this product</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-2 bg-[#F1EDDF] rounded-lg p-2 text-xs">
              <div className="flex-1 truncate">{storefrontProductUrl}</div>
              <Button size="sm" variant="outline" onClick={copy} className="rounded-full" data-testid="copy-link-btn"><Copy size={12}/></Button>
            </div>
            <div className="flex justify-center bg-white p-4 rounded-lg border border-[#E4DFCF]">
              <QRCodeSVG value={storefrontProductUrl} size={192} fgColor="#0F1E15" bgColor="#FFFFFF"/>
            </div>
            <div className="text-xs text-[#4A5A50]">Rich preview link (with Open Graph tags for WhatsApp / social):</div>
            <div className="flex items-center gap-2 bg-[#F1EDDF] rounded-lg p-2 text-xs">
              <div className="flex-1 truncate">{previewUrl}</div>
              <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(previewUrl); toast.success("Preview link copied"); }} className="rounded-full"><Copy size={12}/></Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
